import { supabase } from '@/lib/supabaseClient';

export type TagScope = 'organization' | 'project' | 'folder' | 'list';

export interface TagDefinition {
  id: string;
  name: string;
  color?: string | null;
  scope: TagScope;
  scope_id?: string | null;
  order_index: number;
  created_at: number;
}

function convertFromSupabase(row: Record<string, unknown>): TagDefinition {
  return {
    id: row.id as string,
    name: row.name as string,
    color: row.color as string | null,
    scope: row.scope as TagScope,
    scope_id: row.scope_id as string | null,
    order_index: (row.order_index as number) ?? 0,
    created_at: row.created_at as number,
  };
}

export async function fetchTags(scope: TagScope, scopeId: string): Promise<TagDefinition[]> {
  const { data, error } = await supabase
    .from('tags')
    .select('*')
    .eq('scope', scope)
    .eq('scope_id', scopeId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return (data || []).map(convertFromSupabase);
}

export async function fetchTagsForContext(params: {
  orgId?: string;
  spaceId?: string;
  listId?: string;
}): Promise<(TagDefinition & { origin: 'org' | 'space' | 'list' })[]> {
  const results: (TagDefinition & { origin: 'org' | 'space' | 'list' })[] = [];

  const queries: Promise<void>[] = [];

  if (params.orgId) {
    queries.push(
      fetchTags('organization', params.orgId).then(tags =>
        tags.forEach(t => results.push({ ...t, origin: 'org' }))
      )
    );
  }
  if (params.spaceId) {
    queries.push(
      fetchTags('project', params.spaceId).then(tags =>
        tags.forEach(t => results.push({ ...t, origin: 'space' }))
      )
    );
  }
  if (params.listId) {
    queries.push(
      fetchTags('list', params.listId).then(tags =>
        tags.forEach(t => results.push({ ...t, origin: 'list' }))
      )
    );
  }

  await Promise.all(queries);
  return results;
}

export async function createTag(tag: Omit<TagDefinition, 'id'>): Promise<TagDefinition> {
  const { data, error } = await supabase
    .from('tags')
    .insert({
      name: tag.name,
      color: tag.color ?? null,
      scope: tag.scope,
      scope_id: tag.scope_id ?? null,
      order_index: tag.order_index,
      created_at: tag.created_at,
    })
    .select()
    .single();

  if (error) throw error;
  return convertFromSupabase(data);
}

export async function deleteTag(id: string): Promise<void> {
  const { error } = await supabase.from('tags').delete().eq('id', id);
  if (error) throw error;
}

/**
 * Fetch all unique tag strings used on tasks across an organization.
 * Goes through: org → spaces (projects/lists) → tasks.tags
 */
export async function fetchAllTaskTagsForOrg(orgId: string): Promise<string[]> {
  // Get all space IDs for this org
  const { data: spaces, error: spErr } = await supabase
    .from('spaces')
    .select('id')
    .eq('org_id', orgId);
  if (spErr) throw spErr;
  if (!spaces || spaces.length === 0) return [];

  const spaceIds = spaces.map(s => s.id);

  // Fetch tasks with tags in those spaces (only need the tags column)
  const { data: tasks, error: tErr } = await supabase
    .from('tasks')
    .select('tags')
    .in('space_id', spaceIds)
    .not('tags', 'is', null);
  if (tErr) throw tErr;

  // Collect unique tag names
  const tagSet = new Set<string>();
  for (const t of tasks || []) {
    const tags = t.tags as string[] | null;
    if (tags) tags.forEach(tag => tagSet.add(tag));
  }
  return Array.from(tagSet).sort((a, b) => a.localeCompare(b, 'it'));
}
